import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { ref, getStorage, getDownloadURL } from 'firebase/storage';

import { animatedModal, AnimatedModalProps, Checkbox, Modal } from '@labradorsports/components';
import { DocumentTypes } from '@labradorsports/constants';

import { DocumentIcon } from '../../shared/interface/index.js';
import { Selectors, useAcknowledgeTeamDocumentMutation } from '../../store/index.js';

interface OwnProps {
    document: any;
}

type Props = AnimatedModalProps & OwnProps;

const ViewDocumentModal: FC<Props> = ({ onClose, document }) => {
    const activeTeam = useSelector(Selectors.activeTeam);
    const uid = useSelector(Selectors.uid);
    const [acknowledgeDocument] = useAcknowledgeTeamDocumentMutation();
    const [acknowledged, setAcknowledged] = useState(false);
    const [downloadUrl, setDownloadUrl] = useState('');

    useEffect(() => {
        (async () => {
            if (document.type === DocumentTypes.FILE) {
                const url = await getDownloadURL(ref(getStorage(), document.path));

                setDownloadUrl(url);
            }
        })();
    }, [document]);

    const acknowledgedChange = (evt, checked) => {
        setAcknowledged(checked);
    };

    const submit = async () => {
        if (acknowledged) {
            await acknowledgeDocument({ documentId: document.id, activeTeam });
        }

        onClose();
    };

    const buttons = [
        { text: 'Cancel' },
        {
            text: 'Done',
            variant: 'contained',
            color: 'primary',
            action: submit,
        },
    ];

    const content = (() => {
        if (document.type === DocumentTypes.FILE && document.fileName.endsWith('.pdf') && downloadUrl) {
            return (
                <div className="document-preview">
                    <iframe
                        src={`https://docs.google.com/gview?url=${encodeURIComponent(downloadUrl)}&embedded=true`}
                        title={document.name}
                    />
                    {document.requestAcknowledgement && !document.acknowledgements?.[uid] ? (
                        <div className="acknowledgement-box d-flex justify-content-center">
                            <div className="card">
                                <Checkbox
                                    value={acknowledged}
                                    onChange={acknowledgedChange}
                                    label="I have read and understand this document"
                                />
                            </div>
                        </div>
                    ) : null}
                </div>
            );
        }

        return null;
    })();

    return (
        <Modal
            className="view-document-modal"
            title={document.name}
            icon={<DocumentIcon document={document} />}
            buttons={buttons}
            onClose={onClose}
        >
            {content}
            {downloadUrl ? (
                <a href={downloadUrl} target="_blank" rel="noreferrer">
                    Download
                </a>
            ) : null}
        </Modal>
    );
};

export default animatedModal<Props>(ViewDocumentModal);
